import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Gift, Copy, Check, ExternalLink, Dumbbell } from "lucide-react";
import { Button } from "../components/ui/button";
import { openVitaStrong, VITASTRONG_CODE, VITASTRONG_LOGO, partnerPerksEnabled } from "../lib/rewards";

const PRODUCTS = [
  { key: "bands", emoji: "🏋️" },
  { key: "shaker", emoji: "🥤" },
  { key: "bottle", emoji: "💧" },
  { key: "whey", emoji: "💪" },
];

export default function Rewards() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(VITASTRONG_CODE);
      setCopied(true);
      toast.success(t("rewards.code_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error(t("rewards.copy_failed"));
    }
  };

  // UK-only affiliate — Polish players get an empty state (feature disabled).
  if (!partnerPerksEnabled()) {
    return (
      <div className="max-w-2xl mx-auto py-4 space-y-4" data-testid="rewards-page">
        <div className="flex items-center gap-2">
          <Gift size={20} strokeWidth={1.7} className="text-[#E8A365]" />
          <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl sm:text-2xl font-light text-[#2C332A]">
            {t("rewards.title")}
          </h1>
        </div>
        <div
          data-testid="rewards-empty"
          className="rounded-3xl bg-white border border-[#E6E2D8] p-8 text-center text-[#2C332A]/60"
        >
          <Gift size={28} strokeWidth={1.5} className="mx-auto mb-3 text-[#5F7161]" />
          <p className="text-sm">{t("rewards.none", "No partner rewards available in your region yet. Check back soon!")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-4 space-y-4" data-testid="rewards-page">
      <div className="flex items-center gap-2">
        <Gift size={20} strokeWidth={1.7} className="text-[#E8A365]" />
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl sm:text-2xl font-light text-[#2C332A]">
          {t("rewards.title")}
        </h1>
      </div>

      {/* Partner discount hero card */}
      <div
        data-testid="rewards-partner-card"
        className="relative rounded-3xl p-6 overflow-hidden text-center"
        style={{
          background: "linear-gradient(160deg, #1F2620 0%, #2C332A 55%, #3A2320 100%)",
          border: "1px solid rgba(224,86,73,0.35)",
          boxShadow: "0 16px 36px -20px rgba(0,0,0,0.5)",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 50% 12%, rgba(224,86,73,0.20), transparent 55%)" }}
        />
        <div className="relative flex flex-col items-center gap-3">
          <div className="bg-white rounded-2xl px-5 py-3 shadow-lg">
            <img src={VITASTRONG_LOGO} alt="VitaStrong" className="h-9 object-contain" data-testid="vitastrong-logo" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8A365]/85 font-medium">
            {t("rewards.partner_label")}
          </p>
          <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-medium text-white leading-tight">
            {t("rewards.discount_headline")}
          </h2>
          <p className="text-sm text-white/70 max-w-sm">{t("rewards.discount_body")}</p>

          {/* Discount code chip */}
          <button
            data-testid="rewards-copy-code"
            onClick={copyCode}
            className="mt-1 inline-flex items-center gap-3 rounded-full bg-white/10 border border-white/25 pl-5 pr-3 py-2.5 active:scale-[0.97] transition-transform"
          >
            <span style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg font-semibold tracking-widest text-white">
              {VITASTRONG_CODE}
            </span>
            <span className="grid place-items-center w-8 h-8 rounded-full bg-[#E8A365] text-[#2C332A]">
              {copied ? <Check size={15} strokeWidth={2.4} /> : <Copy size={15} strokeWidth={2} />}
            </span>
          </button>
          <p className="text-[11px] text-white/50">{t("rewards.code_hint")}</p>

          <Button
            data-testid="rewards-shop-btn"
            onClick={openVitaStrong}
            className="mt-2 w-full h-12 rounded-2xl bg-[#E05649] hover:bg-[#C8483C] text-white font-medium"
          >
            <ExternalLink size={16} strokeWidth={2} className="mr-2" />
            {t("rewards.shop_cta")}
          </Button>
        </div>
      </div>

      {/* Curated gear */}
      <div className="rounded-3xl bg-white border border-[#E6E2D8] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Dumbbell size={16} strokeWidth={1.7} className="text-[#5F7161]" />
          <h3 style={{ fontFamily: "Outfit, sans-serif" }} className="text-base font-medium text-[#2C332A]">
            {t("rewards.gear_title")}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PRODUCTS.map((p) => (
            <button
              key={p.key}
              data-testid={`rewards-gear-${p.key}`}
              onClick={openVitaStrong}
              className="flex items-center gap-3 p-3 rounded-2xl bg-[#F7F5F0] border border-[#E6E2D8] text-left active:scale-[0.98] transition-transform"
            >
              <span className="text-xl" aria-hidden="true">{p.emoji}</span>
              <span className="text-sm text-[#2C332A] leading-tight">{t(`rewards.gear.${p.key}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Affiliate disclosure */}
      <p className="text-[11px] text-[#2C332A]/55 leading-relaxed px-1" data-testid="rewards-disclosure">
        {t("rewards.disclosure")}
      </p>
    </div>
  );
}
